/**
 * Bank Statement Hooks
 *
 * React Query hooks for bank statement operations.
 * Uses polling (refetchInterval + refetchOnWindowFocus) for real-time updates.
 *
 * staleTime strategy:
 *   30s  — bank statement lines, PSP suggestions, receipt lines
 *   5min — column mappings, PSP types (rarely change during a session)
 *   Infinity — file previews (immutable for a given file)
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SummaryTotals } from '../pages/_partials/SummaryCards';
import type { BankStatementSummaryFilters, FetchPspSuggestionsParams } from '../services/bankStatements';
import {
  confirmPspReceipt,
  createColumnMapping,
  deleteColumnMapping,
  fetchAvailablePspTypes,
  fetchBankStatementClassificationAudit,
  fetchBankStatementLines,
  fetchBankStatementSummary,
  fetchBankStatementUploadStatus,
  fetchColumnMappings,
  fetchPspReceiptLines,
  fetchPspSettlementSuggestions,
  manualMatchBankStatementLine,
  markLineReconciled,
  previewBankStatement,
  previewCsvHeaders,
  reclassifyBankStatementLine,
  triggerAtlarSync,
  updateColumnMapping,
  uploadBankStatement,
} from '../services/bankStatements';
import { confirmPayoutReconciliation, fetchUnmatchedPayouts } from '../services/payouts';
import type {
  BankColumnMapping,
  BankColumnMappingInput,
  BankStatementClassificationAuditEvent,
  BankStatementFilters,
  BankStatementLinesResponse,
} from '../types/bankStatements';

/**
 * Query keys for bank statements
 */
export const bankStatementsKeys = {
  all: ['bank-statements'] as const,
  lists: () => [...bankStatementsKeys.all, 'list'] as const,
  summary: () => [...bankStatementsKeys.all, 'summary'] as const,
  classificationAudit: (lineId: string) => [...bankStatementsKeys.all, 'classification-audit', lineId] as const,
};

// ---------------------------------------------------------------------------
// Upload Processing Tracker
// ---------------------------------------------------------------------------

export type UploadProcessingStatus = 'processing' | 'checking_ledger' | 'complete' | 'complete_with_warnings' | 'all_duplicates';

export interface UploadProcessingState {
  isProcessing: boolean;
  status: UploadProcessingStatus;
  linesSubmitted: number;
  linesProcessed: number;
  fileUploadId: string | null;
  linesMissingTransfer: number;
  linesMissingTransferIds: string[];
}

const INITIAL_STATE: UploadProcessingState = {
  isProcessing: false,
  status: 'processing',
  linesSubmitted: 0,
  linesProcessed: 0,
  fileUploadId: null,
  linesMissingTransfer: 0,
  linesMissingTransferIds: [],
};

/**
 * Hook to track bank statement upload processing progress via polling.
 *
 * Polls /platform/v1/finance/bank-statements/upload-status every ~2.5s while
 * isProcessing is true. Transitions: processing → checking_ledger → complete |
 * complete_with_warnings | all_duplicates.
 *
 * All-duplicates guard: if processed stays 0 after ~12s, assume all duplicates.
 * Hard safety stop at 60s.
 */
export function useUploadProcessingTracker() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<UploadProcessingState>(INITIAL_STATE);

  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hardTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settlingRef = useRef(false);
  const latestStatusRef = useRef<{ count: number; line_ids: string[] } | null>(null);

  const clearAllTimers = useCallback(() => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
    if (hardTimeoutRef.current) {
      clearTimeout(hardTimeoutRef.current);
      hardTimeoutRef.current = null;
    }
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const stopProcessing = useCallback(
    (finalState: Partial<UploadProcessingState>) => {
      clearAllTimers();
      settlingRef.current = false;
      setState((s) => ({ ...s, ...finalState, isProcessing: false }));
      queryClient.invalidateQueries({ queryKey: bankStatementsKeys.all });
    },
    [clearAllTimers, queryClient]
  );

  const { data: uploadStatus } = useQuery({
    queryKey: ['bs-upload-status', state.fileUploadId],
    queryFn: () => fetchBankStatementUploadStatus(state.fileUploadId!),
    enabled: state.isProcessing && !!state.fileUploadId,
    refetchInterval: 2_500 + Math.floor(Math.random() * 500),
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (uploadStatus) {
      latestStatusRef.current = { count: uploadStatus.count, line_ids: uploadStatus.line_ids };
    }
  }, [uploadStatus]);

  useEffect(() => {
    if (!state.isProcessing || !uploadStatus) return;

    const processed = uploadStatus.processed;

    setState((s) => (s.linesProcessed !== processed ? { ...s, linesProcessed: processed } : s));

    if (state.linesSubmitted > 0 && processed >= state.linesSubmitted && !settlingRef.current) {
      settlingRef.current = true;
      clearAllTimers();
      setState((s) => ({ ...s, status: 'checking_ledger' }));

      settleTimerRef.current = setTimeout(() => {
        const latest = latestStatusRef.current;
        if (latest && latest.count > 0) {
          stopProcessing({
            status: 'complete_with_warnings',
            linesMissingTransfer: latest.count,
            linesMissingTransferIds: latest.line_ids,
          });
        } else {
          stopProcessing({ status: 'complete' });
        }
      }, 2_000);
    }
  }, [uploadStatus, state.isProcessing, state.linesSubmitted, clearAllTimers, stopProcessing]);

  const startTracking = useCallback(
    (linesSubmitted: number, fileUploadId?: string) => {
      clearAllTimers();
      settlingRef.current = false;
      latestStatusRef.current = null;
      setState({
        isProcessing: true,
        status: 'processing',
        linesSubmitted,
        linesProcessed: 0,
        fileUploadId: fileUploadId ?? null,
        linesMissingTransfer: 0,
        linesMissingTransferIds: [],
      });

      // All-duplicates guard: if no progress after ~12s, nothing new was imported
      stallTimerRef.current = setTimeout(() => {
        setState((s) => {
          if (!s.isProcessing || s.linesProcessed > 0) return s;
          return { ...s, status: 'all_duplicates', isProcessing: false };
        });
        queryClient.invalidateQueries({ queryKey: bankStatementsKeys.all });
      }, 12_000);

      // Hard safety stop after 60s
      hardTimeoutRef.current = setTimeout(() => {
        setState((s) => (s.isProcessing ? { ...s, isProcessing: false } : s));
        queryClient.invalidateQueries({ queryKey: bankStatementsKeys.all });
      }, 60_000);
    },
    [clearAllTimers, queryClient]
  );

  const dismiss = useCallback(() => {
    clearAllTimers();
    settlingRef.current = false;
    latestStatusRef.current = null;
    setState(INITIAL_STATE);
  }, [clearAllTimers]);

  useEffect(() => {
    return () => clearAllTimers();
  }, [clearAllTimers]);

  return { ...state, startTracking, dismiss };
}

interface UseBankStatementsOptions {
  filters?: BankStatementFilters;
  operatorId?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch bank statement lines with polling for real-time updates.
 * operatorId is used for query-key isolation and enabled guard only — Platform API derives operator from JWT.
 */
export function useBankStatements(options: UseBankStatementsOptions = {}) {
  const { filters, operatorId, enabled = true } = options;

  return useQuery<BankStatementLinesResponse>({
    queryKey: ['bank-statements', operatorId, filters],
    queryFn: () => fetchBankStatementLines({ filters }),
    enabled: enabled && !!operatorId,
    staleTime: 30 * 1000,
    refetchInterval: () => 30_000 + Math.floor(Math.random() * 9_000),
    refetchOnWindowFocus: true,
  });
}

/** Map API summary response (snake_case) to SummaryTotals. */
function mapSummaryResponseToTotals(res: Awaited<ReturnType<typeof fetchBankStatementSummary>>): SummaryTotals {
  return {
    all: { count: res.all.count },
    needs_classifying: { count: res.needs_classifying.count },
    needs_matching: { count: res.needs_matching.count },
    awaiting_payout: { count: res.awaiting_payout.count },
    pending_review: { count: res.pending_review.count },
    auto_matched: { count: res.auto_matched.count },
    manual_matched: { count: res.manual_matched.count },
    settled: { count: res.settled.count },
  };
}

interface UseBankStatementSummaryOptions {
  filters?: BankStatementSummaryFilters;
  operatorId?: string;
  enabled?: boolean;
}

/**
 * Hook for server-backed line counts on each Bank Statements summary card (full filtered dataset).
 * operatorId is used for query-key isolation and enabled guard only.
 */
export function useBankStatementSummary(options: UseBankStatementSummaryOptions = {}) {
  const { filters, operatorId, enabled = true } = options;
  return useQuery({
    queryKey: ['bank-statements', 'summary', operatorId, filters],
    queryFn: () => fetchBankStatementSummary({ filters }),
    enabled: enabled && !!operatorId,
    staleTime: 30 * 1000,
    refetchInterval: () => 30_000 + Math.floor(Math.random() * 9_000),
    refetchOnWindowFocus: true,
    select: mapSummaryResponseToTotals,
  });
}

/**
 * Hook to get server-side count of actionable (needs review) lines.
 * Uses the summary endpoint and sums actionable card counts (needs_classifying, needs_matching, awaiting_payout, pending_review).
 */
export function useNeedsReviewCount(operatorId?: string, enabled = true) {
  return useQuery({
    queryKey: ['bank-statements', 'needs-review-count', operatorId],
    queryFn: () => fetchBankStatementSummary(),
    enabled: enabled && !!operatorId,
    staleTime: 30 * 1000,
    select: (data) => data.needs_classifying.count + data.needs_matching.count + data.awaiting_payout.count + data.pending_review.count,
  });
}

export function useBankStatementClassificationAudit(lineId?: string | null, enabled = true) {
  return useQuery<BankStatementClassificationAuditEvent[]>({
    queryKey: lineId ? bankStatementsKeys.classificationAudit(lineId) : [...bankStatementsKeys.all, 'classification-audit', 'none'],
    queryFn: () => {
      if (!lineId) throw new Error('lineId required');
      return fetchBankStatementClassificationAudit(lineId);
    },
    enabled: enabled && !!lineId,
    staleTime: 30 * 1000,
  });
}

interface UseColumnMappingsOptions {
  operatorId?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch column mapping templates.
 * operatorId is used for query-key isolation and enabled guard only.
 */
export function useColumnMappings(options: UseColumnMappingsOptions = {}) {
  const { operatorId, enabled = true } = options;

  return useQuery({
    queryKey: ['column-mappings', operatorId],
    queryFn: () => fetchColumnMappings(),
    enabled: enabled && !!operatorId,
    staleTime: 5 * 60 * 1000,
  });
}

interface UsePreviewBankStatementOptions {
  file: File | null;
  fileType: 'mt940' | 'csv';
  columnMappingId?: string;
}

/**
 * Hook to preview a bank statement file
 */
export function usePreviewBankStatement(options: UsePreviewBankStatementOptions) {
  const { file, fileType, columnMappingId } = options;

  return useQuery({
    queryKey: ['bank-statement-preview', file?.name, file?.size, file?.lastModified, fileType, columnMappingId],
    queryFn: () => {
      if (!file) throw new Error('No file provided');
      return previewBankStatement(file, fileType, columnMappingId);
    },
    enabled: !!file && (fileType === 'mt940' || !!columnMappingId),
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Hook to preview CSV headers for column mapping
 */
export function usePreviewCsvHeaders(file: File | null) {
  return useQuery({
    queryKey: ['csv-headers-preview', file?.name, file?.size, file?.lastModified],
    queryFn: () => {
      if (!file) throw new Error('No file provided');
      return previewCsvHeaders(file);
    },
    enabled: !!file,
    staleTime: Infinity,
    retry: false,
  });
}

interface UseUploadBankStatementOptions {
  onSuccess?: (data: import('../types/bankStatements').BankStatementUploadResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to upload a bank statement file
 */
export function useUploadBankStatement(options: UseUploadBankStatementOptions = {}) {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, fileType, columnMappingId }: { file: File; fileType: 'mt940' | 'csv'; columnMappingId?: string }) =>
      uploadBankStatement(file, fileType, columnMappingId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bankStatementsKeys.all });
      queryClient.removeQueries({ queryKey: ['bank-statement-preview'] });
      queryClient.removeQueries({ queryKey: ['csv-headers-preview'] });
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}

interface UseCreateColumnMappingOptions {
  onSuccess?: (data: BankColumnMapping) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to create a column mapping template
 */
export function useCreateColumnMapping(options: UseCreateColumnMappingOptions = {}) {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ input }: { input: BankColumnMappingInput }) => createColumnMapping(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['column-mappings'] });
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}

interface UseUpdateColumnMappingOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to update a column mapping template
 */
export function useUpdateColumnMapping(options: UseUpdateColumnMappingOptions = {}) {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mappingId, input }: { mappingId: string; input: Partial<BankColumnMappingInput> }) =>
      updateColumnMapping(mappingId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['column-mappings'] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}

interface UseDeleteColumnMappingOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to delete a column mapping template
 */
export function useDeleteColumnMapping(options: UseDeleteColumnMappingOptions = {}) {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappingId: string) => deleteColumnMapping(mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['column-mappings'] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// Reclassify & Manual Match Hooks
// ---------------------------------------------------------------------------

interface UseReclassifyOptions {
  onSuccess?: (_data: unknown, variables: { lineId: string; classificationType: string }) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to reclassify a bank statement line.
 * Dispatches task; polling will pick up the change on the next interval.
 */
export function useReclassifyBankStatement(options: UseReclassifyOptions = {}) {
  const { onSuccess, onError } = options;

  return useMutation({
    mutationFn: ({ lineId, classificationType }: { lineId: string; classificationType: string }) =>
      reclassifyBankStatementLine(lineId, classificationType),
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}

interface UseManualMatchOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to manually match a bank statement line to a case.
 * Dispatches task; polling will pick up the change on the next interval.
 */
export function useManualMatchBankStatement(options: UseManualMatchOptions = {}) {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineId, caseId, notes }: { lineId: string; caseId: string; notes?: string }) =>
      manualMatchBankStatementLine(lineId, caseId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bankStatementsKeys.all });
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// Atlar Sync
// ---------------------------------------------------------------------------

interface UseAtlarSyncOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useAtlarSync(options: UseAtlarSyncOptions = {}) {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerAtlarSync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bankStatementsKeys.all });
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// PSP Settlement Suggestions & Confirmation
// ---------------------------------------------------------------------------

export function usePspSettlementSuggestions(lineId: string | null, params?: FetchPspSuggestionsParams, enabled = true) {
  return useQuery({
    queryKey: ['psp-settlement-suggestions', lineId, params],
    queryFn: () => {
      if (!lineId) throw new Error('lineId required');
      return fetchPspSettlementSuggestions(lineId, params);
    },
    enabled: enabled && !!lineId,
    staleTime: 30 * 1000,
  });
}

export function useAvailablePspTypes(enabled = true) {
  return useQuery({
    queryKey: ['psp-types'],
    queryFn: fetchAvailablePspTypes,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePspReceiptLines(limit = 50, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['psp-receipt-lines', limit],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => fetchPspReceiptLines(pageParam, limit),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function usePspReceiptLinesCount(enabled = true) {
  return useQuery({
    queryKey: ['psp-receipt-lines', 'count'],
    queryFn: () => fetchPspReceiptLines(undefined, 100),
    enabled,
    staleTime: 30 * 1000,
    select: (data) => data.data.length,
  });
}

interface UseConfirmPspReceiptOptions {
  onSuccess?: (data: { matched_count?: number; bank_receipts_created?: number }) => void;
  onError?: (error: Error) => void;
}

export function useConfirmPspReceipt(options: UseConfirmPspReceiptOptions = {}) {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineId, settlementIds }: { lineId: string; settlementIds?: string[] }) => confirmPspReceipt(lineId, settlementIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bankStatementsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['psp-receipt-lines'] });
      queryClient.invalidateQueries({ queryKey: ['psp-settlement-suggestions'] });
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// Payout Reconciliation
// ---------------------------------------------------------------------------

export function useUnmatchedPayouts(operatorId?: string, options?: { enabled?: boolean }) {
  const extraEnabled = options?.enabled ?? true;
  return useQuery({
    queryKey: ['payouts', 'unmatched', operatorId],
    queryFn: () => fetchUnmatchedPayouts(),
    select: (res) => res.payouts,
    staleTime: 30_000,
    enabled: !!operatorId && extraEnabled,
  });
}

interface UseConfirmPayoutReconciliationOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useConfirmPayoutReconciliation(options: UseConfirmPayoutReconciliationOptions = {}) {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payoutId, bankStatementLineId }: { payoutId: string; bankStatementLineId: string }) =>
      confirmPayoutReconciliation(payoutId, bankStatementLineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bankStatementsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['payouts'] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}

interface UseMarkLineReconciledOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useMarkLineReconciled(options: UseMarkLineReconciledOptions = {}) {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineId, notes }: { lineId: string; notes?: string }) => markLineReconciled(lineId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bankStatementsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['payouts'] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}
